
class VersionCheckerService {
  constructor() {
    this.currentAppJsFilename = null;
    this.timeoutId = null;
    this.isChecking = false;
    this.isStopped = false;
    this.checkInterval = 60000; // 60 seconds
  }

  /**
   * Extract the current app.js filename from the DOM
   * Looks for script tags matching app.js or app.{hash}.js pattern
   */
  extractCurrentAppJsFilename() {
    const scripts = document.querySelectorAll('script[src]');
    
    for (let script of scripts) {
      const src = script.getAttribute('src');
      if (src) {
        // Match app.js or app.{hash}.js pattern
        const match = src.match(/\/app(\.\w+)?\.js/);
        if (match) {
          // Extract just the filename part
          const filename = src.split('/').pop();
          this.currentAppJsFilename = filename;
          return filename;
        }
      }
    }
    
    // Fallback: try to find any script with 'app' in the name
    for (let script of scripts) {
      const src = script.getAttribute('src');
      if (src && src.includes('app') && src.endsWith('.js')) {
        const filename = src.split('/').pop();
        this.currentAppJsFilename = filename;
        return filename;
      }
    }
    
    return null;
  }

  /**
   * Parse HTML string to extract script tag filenames
   */
  extractScriptFilenamesFromHtml(htmlString) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const scripts = doc.querySelectorAll('script[src]');
    const filenames = [];
    
    scripts.forEach(script => {
      const src = script.getAttribute('src');
      if (src) {
        const filename = src.split('/').pop();
        filenames.push(filename);
      }
    });
    
    return filenames;
  }

  /**
   * Fetch index.html and check if current app.js filename exists
   */
  async checkForNewVersion() {
    if (this.isStopped) {
      return;
    }

    if (this.isChecking) {
      // Prevent concurrent checks
      return;
    }

    this.isChecking = true;

    try {
      // Fetch index.html with cache-busting using native fetch
      const url = `/index.html?t=${Date.now()}`;
      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-cache'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const htmlContent = await response.text();
      const scriptFilenames = this.extractScriptFilenamesFromHtml(htmlContent);
      
      // Check if current app.js filename exists in the new HTML
      const currentFileExists = scriptFilenames.includes(this.currentAppJsFilename);

      if (!currentFileExists && this.currentAppJsFilename) {
        // New version detected - trigger reload dialog
        this.onNewVersionDetected();
      } else {
        // Version is still current - schedule next check
        this.scheduleNextCheck();
      }
    } catch (error) {
      // Network error or other issue - continue checking silently
      console.error('Version check failed:', error);
      // Schedule next check even on error
      this.scheduleNextCheck();
    } finally {
      this.isChecking = false;
    }
  }

  /**
   * Schedule the next version check using setTimeout
   */
  scheduleNextCheck() {
    if (this.isStopped) {
      return;
    }

    this.timeoutId = setTimeout(() => {
      this.checkForNewVersion();
    }, this.checkInterval);
  }

  /**
   * Called when a new version is detected
   */
  onNewVersionDetected() {
    // Stop further checks
    this.stop();
    
    // Trigger reload dialog via Vue instance
    // We'll need to access Vue instance, so we'll use a callback pattern
    if (this.onVersionMismatchCallback) {
      this.onVersionMismatchCallback();
    }
  }

  /**
   * Start version checking
   */
  start(onVersionMismatchCallback) {
    // Extract current filename first
    if (!this.currentAppJsFilename) {
      this.extractCurrentAppJsFilename();
    }

    if (!this.currentAppJsFilename) {
      console.warn('Could not extract app.js filename. Version checking disabled.');
      return;
    }

    this.isStopped = false;
    this.onVersionMismatchCallback = onVersionMismatchCallback;
    
    // Schedule first check
    this.scheduleNextCheck();
  }

  /**
   * Stop version checking
   */
  stop() {
    this.isStopped = true;
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  /**
   * Get current app.js filename
   */
  getCurrentAppJsFilename() {
    return this.currentAppJsFilename;
  }
}

// Export singleton instance
export default new VersionCheckerService();
